<script setup lang="ts">
import { computed, ref } from 'vue'
import dayjs from '@/lib/dayjs'
import StudentModeBadge from './StudentModeBadge.vue'
import { useNavigation } from './useNavigation'
import type { Student2Context } from '@/types/student2'

const props = defineProps<{ context: Student2Context }>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'change-mode', mode: string, tempFreeMode?: boolean): void
}>()

const { getLink: $getLink, gotoOnClick: $gotoOnClick } = useNavigation()

const isOpen = ref(false)
const willChangeToFree = ref(false)
const tempFreeMode = ref(true)

const showFreeModeExpiration = computed(() => {
  if (props.context.student.free_mode_until == null) return false
  const until = dayjs.unix(props.context.student.free_mode_until)
  return Math.abs(dayjs().diff(until, 'days')) < 2
})

const formatDateTime = (val: number | null) => {
  if (val == null) return ''
  return dayjs.unix(val).format('DD.MM HH:mm')
}

const $open = () => {
  isOpen.value = true
}

const close = () => {
  isOpen.value = false
  willChangeToFree.value = false
  tempFreeMode.value = true
  emit('close')
}

const changeMode = (mode: string, temp?: boolean) => {
  close()
  emit('change-mode', mode, temp)
}

const openFreeModeConfirm = () => {
  willChangeToFree.value = true
}

const closeFreeModeConfirm = () => {
  willChangeToFree.value = false
}

const handleTeacherLinkClick = (event: Event) => {
  $gotoOnClick(event)
  isOpen.value = false
  emit('close')
}

defineExpose({ $open })
</script>

<template>
  <v-rich-modal v-model="isOpen" title="Режим кабинета ученика" @update:model-value="close">
    <ul v-if="willChangeToFree" class="student-change-mode-modal">
      <li class="student-change-mode-modal__card student-change-mode-modal__card_gray-bg">
        <div class="flex-spacer mr-6">
          <p class="text-body-bold">Перейти на самостоятельный режим</p>

          <v-radio-group
            v-model="tempFreeMode"
            class="mt-2 student-change-mode-modal__radio-group"
            :items="[
              { value: true, text: 'На 24 часа' },
              { value: false, text: 'На всё время' }
            ]"
          />
        </div>

        <StudentModeBadge mode="free" />
      </li>

      <div class="d-flex">
        <v-button mode="outlined large block" @click="closeFreeModeConfirm"> Отмена </v-button>

        <div class="mx-1" />

        <v-button mode="primary filled large block" @click="changeMode('free', tempFreeMode)"> Сменить режим </v-button>
      </div>
    </ul>
    <ul v-else class="student-change-mode-modal">
      <li class="student-change-mode-modal__card">
        <StudentModeBadge mode="auto" />

        <div class="flex-spacer">
          <p class="text-body-bold">Автоматический (рекомендуем)</p>
          <p class="mt-1 gray-text">Для тех родителей, кто хочет доверить контроль сайту</p>

          <ul class="mt-2">
            <li class="d-flex align-center">
              <v-icon class="green-text mr-1">mdi-check</v-icon>
              Контроль активного прослушивания аудиоуроков
            </li>
            <li class="d-flex align-center mt-1">
              <v-icon class="green-text mr-1">mdi-check</v-icon>
              Автоматическая проверка контрольных заданий
            </li>
            <li class="d-flex align-center mt-1">
              <v-icon class="green-text mr-1">mdi-check</v-icon>
              Поэтапное завершение юнитов
            </li>
          </ul>
        </div>

        <v-button v-if="!context.student.free_mode" mode="success filled large"> Активен </v-button>
        <v-button v-else mode="primary outlined large" @click="changeMode('auto')"> Перейти </v-button>
      </li>

      <li class="student-change-mode-modal__card">
        <StudentModeBadge mode="free" />

        <div class="flex-spacer">
          <p class="text-body-bold">Самостоятельный</p>
          <p class="mt-1 gray-text">Для обучения без контроля и ограничений</p>

          <ul class="mt-2">
            <li class="d-flex align-center">
              <v-icon class="red-text mr-1">mdi-minus</v-icon>
              Отсутствует контроль знаний
            </li>
            <li class="d-flex align-center mt-1">
              <v-icon class="red-text mr-1">mdi-minus</v-icon>
              Нет ограничений к разделам кабинета
            </li>
            <li class="d-flex align-center mt-1">
              <v-icon class="red-text mr-1">mdi-minus</v-icon>
              Нет статистики активности
            </li>
          </ul>
        </div>

        <div v-if="context.student.free_mode" class="d-flex flex-column justify-center">
          <v-button mode="success filled large"> Активен </v-button>
          <p v-if="showFreeModeExpiration" class="mt-1 text-center">
            до {{ formatDateTime(context.student.free_mode_until) }}
          </p>
        </div>
        <v-button v-else mode="primary outlined large" @click="openFreeModeConfirm"> Перейти </v-button>
      </li>

      <!-- FIXME: temporarily hidden -->
      <li v-if="false" class="student-change-mode-modal__card student-change-mode-modal__card_gray-bg">
        <StudentModeBadge mode="teacher" primary />

        <div>
          <p class="text-body-bold">Обучение с учителем</p>
          <p class="mt-1">
            Это дополнительные возможности для глубокого и структурированного обучения вашего ребенка: онлайн
            отслеживание активности, отчеты об успеваемости, полное завершение юнитов.
          </p>

          <v-link
            mode="primary"
            class="text-body-bold mt-2"
            :href="$getLink({ tab: 'mode-info', hash: '#teacher-link' })"
            @click="handleTeacherLinkClick"
          >
            Подробнее о прикреплении к учителю
          </v-link>
        </div>
      </li>
    </ul>
  </v-rich-modal>
</template>

<style lang="scss">
.student-change-mode-modal {
  max-width: 780px;

  display: flex;
  flex-direction: column;
  gap: 16px;

  &__card {
    border: 1px solid $lightGray;
    border-radius: 16px;
    padding: 24px;
    display: flex;
    gap: 16px;

    &_gray-bg {
      background: $lightenGray;
    }

    .v-button {
      align-self: center;
    }
  }

  &__radio-group {
    display: flex;
    gap: 16px;
  }
}
</style>
